import { Component, OnInit } from '@angular/core';
import { SharedService } from '../../services/shared.service';
import { FilterModel } from '@app/models/filter-model';
import { IndicationGroup } from '@app/models/indication';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { asapScheduler } from 'rxjs';

// highcharts imports
import * as Highcharts from 'highcharts';
import HighchartsMore from 'highcharts/highcharts-more';
import HighchartsExporting from 'highcharts/modules/exporting';
import HighchartsData from 'highcharts/modules/data';
import HighchartsVenn from 'highcharts/modules/venn';
require('highcharts/themes/dark-unica')(Highcharts);

import highcharts3D from 'highcharts/highcharts-3d';
import _ from 'lodash';
highcharts3D(Highcharts);
HighchartsMore(Highcharts);
HighchartsExporting(Highcharts);
HighchartsData(Highcharts);
HighchartsVenn(Highcharts);

Highcharts.setOptions({
  lang: {
    thousandsSep: ','
  }
});

@Component({
  selector: 'app-results-charts',
  templateUrl: './results-charts.component.html',
  styleUrls: ['./results-charts.component.css']
})
export class ResultsChartsComponent implements OnInit {

  Highcharts: typeof Highcharts = Highcharts;
  updateFlag = false;

  constructor(
    private readonly sharedService: SharedService,
  ) { }

  chartOptions: any = {
    chart: {
      renderTo: 'container',
      type: 'venn',
      backgroundColor: 'transparent'
    },
    title: {
      text: ''
    },
    plotOptions: {
      venn: {
        showInLegend: true
      }
    },
    series: [{
      type: 'venn',
      data: []
    }]
  };

  pieChartOptions: any = {
      chart: {
        renderTo: 'pieContainer',
        type: 'pie',
        backgroundColor: 'transparent',
        options3d: {
          enabled: true,
          alpha: 35,
          beta: 0
       }
    },
    title: {
        text: '',
        align: 'left'
    },
    tooltip: {
        pointFormat: '<b>{point.percentage:.1f}%</b> ({point.value})'
    },
    accessibility: {
        point: {
            valueSuffix: '%'
        }
    },
    plotOptions: {
        pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            depth: 100,
            dataLabels: {
                enabled: false
            },
            showInLegend: true
        }
    },
    series: [{
      name: '',
      colorByPoint: true,
      data: []
    }]
  };

  chartYearsList: string[] = [];
  defaultChartYear: string = '2020';
  selectedChartYear: string = this.defaultChartYear;

  loading = false;
  resultMessage = '';
  gridColumns: any[] = [];
  queryData: any[] = [];
  rawPivotTableData: any[] = [];
  formattedChartData: any[] = [];
  sumValues: any[] = [];
  gridData: GridDataResult;
  allIndications: IndicationGroup[] = [];
  selectedDataSets: any = [];
  filterModel: FilterModel;
  selectedIndicationsCount: number = 0;

  activeQueryName = '';

  ngOnInit(): void {

    this.sharedService.selectedDataSets.subscribe(data => {
      if (data) {
        this.selectedDataSets = data;
      }
    });

    this.sharedService.rawResultsData.subscribe(
      data => {
        if (data !== null) {
          this.selectedChartYear = this.defaultChartYear;
          this.rawPivotTableData = data.pivotTableData;
          this.chartYearsList = data.yearsList;
          this.selectedIndicationsCount = this.rawPivotTableData && this.rawPivotTableData.length > 0 ? this.rawPivotTableData.length : 0;
          this.resultMessage = data.hasOwnProperty('errorMessage') && data['errorMessage'] !== null ? data['errorMessage'] : '';
          this.updateChartData();
        }
      }
    );

    this.sharedService.activeQueryName.subscribe(data => {
      if (data) {
        this.activeQueryName = data;
      }
    });
  }

  setVennChart(): void {
    const formatter = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,      
      maximumFractionDigits: 2,
    });

    // let unorderedVennData = [];
    let vennData = [];
    var pivotSum = 0;
    let selectedYearCol = `col_${this.selectedChartYear}`;
    this.rawPivotTableData.map(i => pivotSum += i[selectedYearCol]);
    this.rawPivotTableData.sort((a, b) => a.indication - b.indication).forEach((i, index) => {
      let item = {
        dataValue: i[selectedYearCol],
        indication: i.indication,
        recordCount: i.recordCount,
        dataPercent: formatter.format((i[selectedYearCol] / pivotSum) * 100)
      };
      const multipleSets = item.indication.split(' - ');
      if (multipleSets && multipleSets.length > 1) {
        for(let i = 0; i < multipleSets.length; i++) {
          multipleSets[i] = multipleSets[i].trim();
        };
        item['sets'] = multipleSets;
      } else {
        item['sets'] = [item.indication];
      }
      // unorderedVennData.push(item);
      vennData.push(item)
    });
    // unorderedVennData.forEach(i => {
    //   if (!i.hasOwnProperty('sets')) {
    //     vennData.push(i);
    //   }
    // });
    // unorderedVennData.forEach(i => {
    //   if (i.hasOwnProperty('sets')) {
    //     vennData.push(i);
    //   }
    // });
    
    var vennDataTotalPercent = 0;
    vennData.forEach((i, index) => {
      i['index'] = index + 1;
      vennDataTotalPercent += Number(i.dataPercent);
    });

    let amountAbove100 = vennDataTotalPercent - 100.00;
    if (amountAbove100 > 0) {
      vennData = vennData.sort((a, b) => b.dataValue - a.dataValue);
      vennData[0].dataPercent = vennData[0].dataPercent - amountAbove100;
    }

    this.setHighchartsVennData(vennData);
  }

  setHighchartsVennData(data: any): void {
    let seriesData = [];
    data.forEach((i, index) => { 
      let series = {
        name: `${i.indication} (${i.dataPercent}%)`,
        value: i.dataValue,
        sets: i.hasOwnProperty('sets') ? i.sets : [i.indication]
      };
      seriesData.push(series);
    });
    this.chartOptions.series = [{
      type: 'venn',
      name: this.activeQueryName,
      data: seriesData
    }];
    this.updateFlag = true;
  }

  setPieChart(): void {
    const formatter = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,      
      maximumFractionDigits: 2,
    });

    let pieData = [];
    var pivotSum = 0;
    let selectedYearCol = `col_${this.selectedChartYear}`;
    this.rawPivotTableData.map(i => pivotSum += i[selectedYearCol]);
    this.rawPivotTableData.sort((a, b) => a.indication - b.indication).forEach((i, index) => {
      let item = {
        y: Number(formatter.format((i[selectedYearCol] / pivotSum) * 100)),
        name: i.indication,
        value: i[selectedYearCol].toLocaleString()
      };
      pieData.push(item);
    });
    
    this.setHighchartsPieData(pieData);
  }

  setHighchartsPieData(data: any): void {
    this.pieChartOptions.series = [{
      name: this.activeQueryName,
      colorByPoint: true,
      data: data
    }];
    this.updateFlag = true;
  }

  updateChartData(): void {
    // asapScheduler.schedule(() => {
      setTimeout(() => {
        if (this.selectedIndicationsCount > 1 && this.selectedIndicationsCount < 8) {
          this.setVennChart();
        } else if (this.selectedIndicationsCount >= 8) {
          this.setPieChart();
        }
      }, 10);
    // });
  }

  addLinebreakToString(i: string): string {
    return i.replace(/- /g, "-\n");
  }

  sumArrayValues(array): any {
    return array.reduce((a, b) => {
      return a + b;
    }, 0);
  }

  // round(value, exp) {
  //   if (typeof exp === 'undefined' || +exp === 0)
  //     return Math.round(value);
  
  //   value = +value;
  //   exp = +exp;
  
  //   if (isNaN(value) || !(typeof exp === 'number' && exp % 1 === 0))
  //     return NaN;
  
  //   // Shift
  //   value = value.toString().split('e');
  //   value = Math.round(+(value[0] + 'e' + (value[1] ? (+value[1] + exp) : exp)));
  
  //   // Shift back
  //   value = value.toString().split('e');
  //   return +(value[0] + 'e' + (value[1] ? (+value[1] - exp) : -exp));
  // }
}
